import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarDays, CalendarRange, List, UserRound, Users } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import FilterSelect from "../../../../shared/ui/form/FilterSelect";
import {
    ActiveFilterChips,
    FilterChip,
    FiltersToggle,
} from "../../../../shared/ui/form/FilterControls";
import Label from "../../../../shared/ui/form/Label";
import { Pagination } from "../../../../shared/ui/common/Pagination";
import { Modal } from "../../../../shared/ui/modal";
import {
    useCenter,
    useCenterAgenda,
    useCenterAgendaCalendar,
    useCenterMembers,
    useCenterPatients,
    useMoveAppointment,
} from "../../../center";
import type { CenterAppointment } from "../../../center";
import {
    AgendaCalendarView,
    assignTherapistColors,
    calendarRange,
    minuteLabel,
    minutesOfTime,
} from "../../domain/agendaCalendar";
import AgendaCalendar from "../components/AgendaCalendar";
import { groupByPerson, WHOLE_CASELOAD } from "../../domain/caseloadPeople";
import CenterAdminShell from "../components/CenterAdminShell";
import {
    DateRangeFilter as CenterDateRangeFilter,
    FILTER_LABEL,
} from "../../../../shared/components/reports";
import SessionEventsPanel from "../components/SessionEventsPanel";
import { centerGroupLabel } from "../centerGroupLabel";
import { toLocalDateKey } from "../../../../shared/utils/date";

// The sessions' statuses, as their values; the words are the session module's.
const SESSION_STATUSES = ["pending", "finished", "cancelled"] as const;
const PAYMENT_STATUSES = ["pending", "paid"] as const;
const ORIGINS = ["true", "false"] as const;
const PAGE_SIZE = 25;

type AgendaView = "list" | AgendaCalendarView;

const VIEWS = [
    { view: "list", icon: List },
    { view: "week", icon: CalendarRange },
    { view: "month", icon: CalendarDays },
] as const;

function minutesSinceMidnight(at: Date = new Date()): number {
    return at.getHours() * 60 + at.getMinutes();
}

function isoDay(offsetDays: number): string {
    const day = new Date();
    day.setDate(day.getDate() + offsetDays);
    return toLocalDateKey(day);
}

/**
 * The centre's week.
 *
 * Times, people and status. No meeting link and no title — a link is a way
 * into somebody's session, and a title says what it is about.
 */
export default function CenterAgendaPage() {
    const { t } = useTranslation();
    const { center, loading } = useCenter();
    const { members } = useCenterMembers(center?.id, true);

    const [from, setFrom] = useState(isoDay(0));
    const [to, setTo] = useState(isoDay(7));
    // Several therapists' agendas side by side, to find a gap between them.
    const [therapistIds, setTherapistIds] = useState<string[]>([]);
    const [sessionStatus, setSessionStatus] = useState("");
    const [paymentStatus, setPaymentStatus] = useState("");
    const [origin, setOrigin] = useState("");
    // One person, however many records the centre holds for them.
    const [personFilter, setPersonFilter] = useState("");
    const [offset, setOffset] = useState(0);
    // The table, or the week or month as a calendar around `anchor`. The month
    // first: the centre's agenda is read for its shape before its rows.
    const [view, setView] = useState<AgendaView>("month");
    const [anchor, setAnchor] = useState(isoDay(0));
    // The calendar card whose details are open, if any.
    const [opened, setOpened] = useState<CenterAppointment | null>(null);
    // The row whose session history is open, if any.
    const [eventsOf, setEventsOf] = useState<{ sessionId: string; heading: string } | null>(null);
    // Whether the less used filters are open under "Filtros".
    const [expanded, setExpanded] = useState(false);

    const clearFilters = () => {
        setFrom(isoDay(0));
        setTo(isoDay(7));
        setTherapistIds([]);
        setSessionStatus("");
        setPaymentStatus("");
        setOrigin("");
        setPersonFilter("");
        setAnchor(isoDay(0));
    };

    const { patients } = useCenterPatients(center?.id, { limit: WHOLE_CASELOAD });
    const people = useMemo(() => groupByPerson(patients), [patients]);
    const personOptions = people.map((person) => ({
        value: person.key,
        label: `${person.firstName} ${person.lastName}`,
    }));
    const patientIds = useMemo(
        () =>
            people
                .find((person) => person.key === personFilter)
                ?.records.map((record) => record.patient_id),
        [people, personFilter]
    );

    const range = useMemo(
        () => ({ start: `${from}T00:00:00`, end: `${to}T23:59:59` }),
        [from, to]
    );
    const statuses = useMemo(
        () => ({
            sessionStatus: sessionStatus || undefined,
            paymentStatus: paymentStatus || undefined,
            scheduledInAlchely: origin || undefined,
        }),
        [sessionStatus, paymentStatus, origin]
    );
    // Any change of filter starts the list over from its first page.
    useEffect(() => {
        setOffset(0);
    }, [range, therapistIds, statuses, patientIds]);
    const isCalendar = view !== "list";
    const {
        appointments,
        total,
        loading: agendaLoading,
    } = useCenterAgenda(center?.id, range, therapistIds, statuses, {
        patientIds,
        offset,
        limit: PAGE_SIZE,
        enabled: !isCalendar,
    });
    const { from: calendarFrom, to: calendarTo } = calendarRange(isCalendar ? view : "week", anchor);
    const calendarQuery = useMemo(
        () => ({ start: `${calendarFrom}T00:00:00`, end: `${calendarTo}T23:59:59` }),
        [calendarFrom, calendarTo]
    );
    const { appointments: calendarAppointments, loading: calendarLoading } =
        useCenterAgendaCalendar(
            center?.id,
            calendarQuery,
            therapistIds,
            statuses,
            patientIds,
            isCalendar
        );

    // Ordered by when each therapist joined, so somebody arriving later takes
    // the next colour rather than shifting everybody else's.
    const therapistColors = useMemo(() => {
        const team = [...members]
            .sort(
                (a, b) =>
                    (a.joined_at ?? "").localeCompare(b.joined_at ?? "") ||
                    a.therapist_id.localeCompare(b.therapist_id)
            )
            .map((member) => member.therapist_id);
        // Somebody no longer on the team may still hold bookings in the range.
        const others = calendarAppointments
            .map((appointment) => appointment.therapist_id)
            .filter((id) => !team.includes(id))
            .sort();
        return assignTherapistColors([...team, ...others]);
    }, [members, calendarAppointments]);
    const colorOf = (id: string) => therapistColors.get(id) ?? "#475569";
    const therapistsInRange = useMemo(
        () => [...new Set(calendarAppointments.map((appointment) => appointment.therapist_id))],
        [calendarAppointments]
    );

    const move = useMoveAppointment(center?.id);

    const [moving, setMoving] = useState<string | null>(null);
    const [target, setTarget] = useState("");
    const [error, setError] = useState<string | null>(null);

    const therapistName = (id: string) => {
        const member = members.find((m) => m.therapist_id === id);
        return member ? `${member.first_name} ${member.last_name}` : "—";
    };

    const subjectOf = (appointment: CenterAppointment) =>
        appointment.group_member_count != null
            ? centerGroupLabel(t, appointment.patient_name, appointment.group_member_count)
            : (appointment.patient_name ?? "—");

    const handleMove = async (appointmentId: string) => {
        setError(null);
        try {
            await move.mutateAsync({ appointmentId, toTherapistId: target });
            setMoving(null);
            setTarget("");
            setOpened(null);
        } catch (err: unknown) {
            const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data
                ?.detail;
            setError(
                detail === "slot_taken"
                    ? t("center.agenda.errorSlotTaken")
                    : t("center.agenda.errorGeneric")
            );
        }
    };

    if (loading || !center) {
        return <p className="p-6 text-sm text-gray-500">{t("common.loading")}</p>;
    }

    // The less used filters, each one a chip while it is set — so an agenda
    // narrowed behind a closed "Filtros" still says why it is narrow.
    const chips: FilterChip[] = [];
    if (personFilter)
        chips.push({
            key: "person",
            label:
                personOptions.find((option) => option.value === personFilter)?.label ??
                personFilter,
        });
    if (sessionStatus)
        chips.push({ key: "sessionStatus", label: t(`session.progressOptions.${sessionStatus}`) });
    if (paymentStatus)
        chips.push({ key: "paymentStatus", label: t(`session.paymentOptions.${paymentStatus}`) });
    if (origin)
        chips.push({
            key: "origin",
            label: t(
                origin === "true" ? "center.agenda.scheduledInAlchely" : "center.agenda.notScheduled"
            ),
        });
    const chipClearers: Record<string, () => void> = {
        person: () => setPersonFilter(""),
        sessionStatus: () => setSessionStatus(""),
        paymentStatus: () => setPaymentStatus(""),
        origin: () => setOrigin(""),
    };
    const clearChip = (key: string) => chipClearers[key]?.();
    // The chips go; the dates and the therapist stay, as they are in view.
    const clearChips = () => chips.forEach((chip) => clearChip(chip.key));

    /*
     * Only the therapist stays in view, beside the dates: seven
     * fields open at once pushed the agenda below the fold, and most visits
     * look at one or a few therapists' week. The rest open under "Filtros", as they do
     * in the meditations.
     */
    const therapistFilter = (
        <div className="space-y-1.5">
            <Label className={FILTER_LABEL}>{t("center.agenda.therapist")}</Label>
            <FilterSelect
                options={members.map((member) => ({
                    value: member.therapist_id,
                    label: `${member.first_name} ${member.last_name}`,
                }))}
                value={therapistIds}
                onChange={(value) => setTherapistIds(value as string[])}
                multiple
                placeholder={t("center.agenda.everyone")}
                searchPlaceholder={t("center.patients.searchTherapist")}
                icon={<UserRound className="h-5 w-5" />}
                className="text-sm"
            />
        </div>
    );

    const toggle = (
        <div className="flex items-end">
            <FiltersToggle
                expanded={expanded}
                count={chips.length}
                onToggle={() => setExpanded((value) => !value)}
            />
        </div>
    );

    const moreFilters = expanded && (
        <div className="mb-5 grid grid-cols-1 gap-4 rounded-2xl border border-gray-100 bg-gray-50/50 p-4 sm:grid-cols-2 lg:grid-cols-4 dark:border-white/[0.05] dark:bg-white/[0.02]">
            <div className="space-y-1.5">
                <Label className={FILTER_LABEL}>{t("center.agenda.patient")}</Label>
                <FilterSelect
                    options={personOptions}
                    value={personFilter}
                    onChange={(value) => setPersonFilter(value as string)}
                    placeholder={t("center.agenda.anyPatient")}
                    searchPlaceholder={t("center.patients.searchPatient")}
                    icon={<Users className="h-5 w-5" />}
                    className="text-sm"
                />
            </div>
            <div className="space-y-1.5">
                <Label className={FILTER_LABEL}>{t("session.progressStatus")}</Label>
                <FilterSelect
                    options={SESSION_STATUSES.map((value) => ({
                        value,
                        label: t(`session.progressOptions.${value}`),
                    }))}
                    value={sessionStatus}
                    onChange={(value) => setSessionStatus(value as string)}
                    placeholder={t("center.agenda.anySessionStatus")}
                    className="text-sm"
                />
            </div>
            <div className="space-y-1.5">
                <Label className={FILTER_LABEL}>{t("session.paymentStatus")}</Label>
                <FilterSelect
                    options={PAYMENT_STATUSES.map((value) => ({
                        value,
                        label: t(`session.paymentOptions.${value}`),
                    }))}
                    value={paymentStatus}
                    onChange={(value) => setPaymentStatus(value as string)}
                    placeholder={t("center.agenda.anyPaymentStatus")}
                    className="text-sm"
                />
            </div>
            <div className="space-y-1.5">
                <Label className={FILTER_LABEL}>{t("center.agenda.origin")}</Label>
                <FilterSelect
                    options={ORIGINS.map((value) => ({
                        value,
                        label: t(value === "true" ? "center.agenda.scheduledInAlchely" : "center.agenda.notScheduled"),
                    }))}
                    value={origin}
                    onChange={(value) => setOrigin(value as string)}
                    placeholder={t("center.agenda.anyOrigin")}
                    className="text-sm"
                />
            </div>
        </div>
    );

    // Giving a booking to another therapist, below the table or in a card's details.
    const movePanel = (
        <div className="mt-5 rounded-xl border border-gray-200 p-4 dark:border-gray-800">
            <h3 className="mb-1 text-sm font-medium text-content-primary">
                {t("center.agenda.moveTitle")}
            </h3>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                {t("center.agenda.moveHint")}
            </p>
            <div className="flex flex-wrap items-end gap-3">
                <div>
                    <Label htmlFor="move-target">{t("center.agenda.therapist")}</Label>
                    <select
                        id="move-target"
                        value={target}
                        onChange={(e) => setTarget(e.target.value)}
                        className="h-11 rounded-lg border border-gray-300 bg-transparent px-3 text-sm dark:border-gray-700"
                    >
                        <option value="">{t("center.patients.choose")}</option>
                        {members.map((member) => (
                            <option key={member.therapist_id} value={member.therapist_id}>
                                {member.first_name} {member.last_name}
                            </option>
                        ))}
                    </select>
                </div>
                <Button
                    onClick={() => moving && handleMove(moving)}
                    disabled={!target || move.isPending}
                    loading={move.isPending}
                >
                    {t("center.agenda.confirmMove")}
                </Button>
            </div>
            {error && (
                <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
                    {error}
                </p>
            )}
        </div>
    );

    const viewSwitcher = (
        <div
            role="group"
            aria-label={t("center.agenda.views.label")}
            className="flex items-center gap-1 rounded-xl bg-gray-100 p-1 dark:bg-white/10"
        >
            {VIEWS.map(({ view: option, icon: Icon }) => (
                <button
                    key={option}
                    type="button"
                    onClick={() => setView(option)}
                    aria-pressed={view === option}
                    title={t(`center.agenda.views.${option}`)}
                    className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors ${
                        view === option
                            ? "bg-white text-brand-600 shadow-sm dark:bg-white/10"
                            : "text-content-tertiary hover:text-gray-600 dark:hover:text-gray-300"
                    }`}
                >
                    <Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{t(`center.agenda.views.${option}`)}</span>
                </button>
            ))}
        </div>
    );

    return (
        <CenterAdminShell center={center}>
            <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h2 className="mb-1 text-lg font-semibold text-content-primary">
                            {t("center.agenda.title")}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t("center.agenda.subtitle")}
                        </p>
                    </div>
                    {viewSwitcher}
                </div>

                {/* The calendar's dates are the week or month on screen, so it
                    takes every filter but the date range. */}
                {isCalendar ? (
                    <div className="mb-5 flex flex-col items-end gap-4 rounded-2xl border border-gray-100 bg-gray-50/50 p-4 sm:flex-row dark:border-white/[0.05] dark:bg-white/[0.02]">
                        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {therapistFilter}
                            {toggle}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={clearFilters}
                            className="h-[42px] w-full sm:w-auto"
                        >
                            {t("common.clear")}
                        </Button>
                    </div>
                ) : (
                    <CenterDateRangeFilter
                        idPrefix="agenda"
                        from={from}
                        to={to}
                        onFromChange={setFrom}
                        onToChange={setTo}
                        onClear={clearFilters}
                    >
                        {[
                            <Fragment key="therapist">{therapistFilter}</Fragment>,
                            <Fragment key="toggle">{toggle}</Fragment>,
                        ]}
                    </CenterDateRangeFilter>
                )}

                {moreFilters}
                {chips.length > 0 && (
                    <div className="mb-5">
                        <ActiveFilterChips chips={chips} onRemove={clearChip} onClear={clearChips} />
                    </div>
                )}

                {isCalendar ? (
                    <>
                        {therapistsInRange.length > 0 && (
                            <ul
                                aria-label={t("center.agenda.calendar.legend")}
                                className="mb-4 flex flex-wrap gap-x-4 gap-y-2"
                            >
                                {therapistsInRange.map((id) => (
                                    <li
                                        key={id}
                                        className="flex items-center gap-1.5 text-sm text-content-secondary"
                                    >
                                        <span
                                            aria-hidden="true"
                                            className="h-3 w-3 rounded-full"
                                            style={{ backgroundColor: colorOf(id) }}
                                        />
                                        {therapistName(id)}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {calendarLoading && (
                            <p className="mb-3 text-sm text-gray-500">{t("common.loading")}</p>
                        )}
                        <AgendaCalendar
                            view={view}
                            anchor={anchor}
                            onAnchorChange={setAnchor}
                            onShowWeek={(day) => {
                                setAnchor(day);
                                setView("week");
                            }}
                            appointments={calendarAppointments}
                            therapistName={therapistName}
                            colorOf={colorOf}
                            subjectOf={subjectOf}
                            todayKey={isoDay(0)}
                            nowMinute={minutesSinceMidnight()}
                            onOpen={(appointment) => {
                                setMoving(null);
                                setError(null);
                                setOpened(appointment);
                            }}
                        />
                    </>
                ) : (
                    <>
        {agendaLoading && <p className="text-sm text-gray-500">{t("common.loading")}</p>}

        {!agendaLoading && appointments.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("center.agenda.empty")}
            </p>
        )}

        {!agendaLoading && appointments.length > 0 && (
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead>
                        <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 dark:border-gray-800 dark:text-gray-400">
                            <th scope="col" className="py-2 pr-4 font-medium">
                                {t("center.agenda.when")}
                            </th>
                            <th scope="col" className="py-2 pr-4 font-medium">
                                {t("center.agenda.who")}
                            </th>
                            <th scope="col" className="py-2 pr-4 font-medium">
                                {t("center.agenda.therapist")}
                            </th>
                            <th scope="col" className="py-2 pr-4 font-medium">
                                {t("center.agenda.status")}
                            </th>
                            <th scope="col" className="py-2 pr-4 font-medium">
                                {t("session.progressStatus")}
                            </th>
                            <th scope="col" className="py-2 pr-4 font-medium">
                                {t("session.paymentStatus")}
                            </th>
                            <th scope="col" className="py-2 font-medium" />
                        </tr>
                    </thead>
                    <tbody>
                        {appointments.map((appointment) => (
                            <tr
                                key={appointment.appointment_id ?? appointment.session_id ?? ""}
                                className="border-b border-gray-100 last:border-0 dark:border-gray-800"
                            >
                                <td className="py-3 pr-4 text-content-primary">
                                    <div>
                                        {new Date(appointment.date).toLocaleDateString()}{" "}
                                        {appointment.hour}
                                    </div>
                                    {/* Booked through the agenda, or written up without a booking. */}
                                    <span
                                        className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${
                                            appointment.scheduled_in_alchely === false
                                                ? "border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
                                                : "border-brand-200 bg-brand-50 text-brand-700 dark:border-brand-500/20 dark:bg-brand-500/10 dark:text-brand-300"
                                        }`}
                                    >
                                        {t(
                                            appointment.scheduled_in_alchely === false
                                                ? "center.agenda.notScheduled"
                                                : "center.agenda.scheduledInAlchely"
                                        )}
                                    </span>
                                </td>
                                <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                                    {subjectOf(appointment)}
                                </td>
                                <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                                    {therapistName(appointment.therapist_id)}
                                </td>
                                <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                                    {appointment.status
                                        ? t(`center.agenda.states.${appointment.status}`, {
                                              defaultValue: appointment.status,
                                          })
                                        : "—"}
                                </td>
                                {/* A booking made before bookings opened a session has none to report. */}
                                <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                                    {appointment.session_status
                                        ? t(`session.progressOptions.${appointment.session_status}`)
                                        : "—"}
                                </td>
                                <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                                    {appointment.payment_status
                                        ? t(`session.paymentOptions.${appointment.payment_status}`)
                                        : "—"}
                                </td>
                                <td className="py-3 text-right">
                                    <div className="flex justify-end gap-2">
                                        {/* A row with a session has a history to show. */}
                                        {appointment.session_id && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() =>
                                                    setEventsOf({
                                                        sessionId: appointment.session_id as string,
                                                        heading: `${new Date(appointment.date).toLocaleDateString()} ${appointment.hour} · ${therapistName(appointment.therapist_id)}`,
                                                    })
                                                }
                                            >
                                                {t("session.events.tab")}
                                            </Button>
                                        )}
                                        {/* Only a booking can be given to another therapist. */}
                                        {appointment.appointment_id && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() =>
                                                    setMoving(
                                                        moving === appointment.appointment_id
                                                            ? null
                                                            : appointment.appointment_id
                                                    )
                                                }
                                            >
                                                {t("center.agenda.move")}
                                            </Button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}

        <Pagination
            offset={offset}
            total={total}
            pageSize={PAGE_SIZE}
            onPrev={() => setOffset((current) => Math.max(current - PAGE_SIZE, 0))}
            onNext={() => setOffset((current) => current + PAGE_SIZE)}
        />


                        {moving && movePanel}
                    </>
                )}
            </section>

            <Modal
                isOpen={opened !== null}
                onClose={() => {
                    setOpened(null);
                    setMoving(null);
                }}
                ariaLabel={t("center.agenda.calendar.details")}
                className="mx-4 w-full max-w-md rounded-2xl bg-surface-raised p-6"
            >
                {opened && (
                    <BookingDetails
                        appointment={opened}
                        subject={subjectOf(opened)}
                        therapist={therapistName(opened.therapist_id)}
                        color={colorOf(opened.therapist_id)}
                        onShowEvents={() => {
                            setEventsOf({
                                sessionId: opened.session_id as string,
                                heading: `${new Date(opened.date).toLocaleDateString()} ${opened.hour} · ${therapistName(opened.therapist_id)}`,
                            });
                            setOpened(null);
                        }}
                        onMove={() =>
                            setMoving(moving === opened.appointment_id ? null : opened.appointment_id)
                        }
                    />
                )}
                {opened && moving && moving === opened.appointment_id && movePanel}
            </Modal>

            <SessionEventsPanel
                centerId={center.id}
                sessionId={eventsOf?.sessionId ?? null}
                heading={eventsOf?.heading ?? ""}
                onClose={() => setEventsOf(null)}
            />
        </CenterAdminShell>
    );
}

interface BookingDetailsProps {
    appointment: CenterAppointment;
    subject: string;
    therapist: string;
    color: string;
    onShowEvents: () => void;
    onMove: () => void;
}

/** A calendar card opened: what the list's row says, and the same actions. */
function BookingDetails({
    appointment,
    subject,
    therapist,
    color,
    onShowEvents,
    onMove,
}: BookingDetailsProps) {
    const { t, i18n } = useTranslation();
    const start = minutesOfTime(appointment.hour);
    const dateLabel = new Date(`${appointment.date.slice(0, 10)}T12:00:00`).toLocaleDateString(
        i18n.language,
        { weekday: "long", day: "numeric", month: "long", year: "numeric" }
    );
    const rows: { label: string; value: string }[] = [
        {
            label: t("center.agenda.status"),
            value: appointment.status
                ? t(`center.agenda.states.${appointment.status}`, { defaultValue: appointment.status })
                : "—",
        },
        {
            label: t("session.progressStatus"),
            value: appointment.session_status
                ? t(`session.progressOptions.${appointment.session_status}`)
                : "—",
        },
        {
            label: t("session.paymentStatus"),
            value: appointment.payment_status
                ? t(`session.paymentOptions.${appointment.payment_status}`)
                : "—",
        },
        {
            label: t("center.agenda.origin"),
            value: t(
                appointment.scheduled_in_alchely === false
                    ? "center.agenda.notScheduled"
                    : "center.agenda.scheduledInAlchely"
            ),
        },
    ];

    return (
        <div className="mb-4">
            <h2 className="pr-12 text-lg font-semibold text-content-primary">{subject}</h2>
            <p className="mb-1 text-sm text-content-secondary first-letter:uppercase">
                {dateLabel} · {minuteLabel(start)} – {minuteLabel(start + appointment.duration)}
            </p>
            <p className="mb-4 flex items-center gap-1.5 text-sm text-content-secondary">
                <span
                    aria-hidden="true"
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: color }}
                />
                {therapist}
            </p>
            <dl className="mb-5 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {rows.map((row) => (
                    <div key={row.label}>
                        <dt className="text-xs text-content-tertiary">{row.label}</dt>
                        <dd className="text-content-primary">{row.value}</dd>
                    </div>
                ))}
            </dl>
            <div className="flex flex-wrap gap-2">
                {appointment.session_id && (
                    <Button size="sm" variant="outline" onClick={onShowEvents}>
                        {t("session.events.tab")}
                    </Button>
                )}
                {appointment.appointment_id && (
                    <Button size="sm" variant="outline" onClick={onMove}>
                        {t("center.agenda.move")}
                    </Button>
                )}
            </div>
        </div>
    );
}
